import { useEffect, useRef, useState } from 'react';

const SPINNER_DELAY = 180;
const MIN_VISIBLE_TIME = 300;

interface UseSmartSpinnerLoadingOptions {
  isLoading: boolean;
  isFetching: boolean;
}

export const useSmartSpinnerLoading = ({
  isLoading,
  isFetching,
}: UseSmartSpinnerLoadingOptions): boolean => {
  const [showLoader, setShowLoader] = useState(false);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spinnerStartTimeRef = useRef<number | null>(null);
  const isLoadingRef = useRef(false);

  // Track current loading state for timeout callbacks
  useEffect(() => {
    isLoadingRef.current = isFetching || isLoading;
  }, [isFetching, isLoading]);

  useEffect(() => {
    const isSearching = isFetching || isLoading;

    if (isSearching) {
      // Cancel any pending hide operations
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      // Only set up show timeout if loader isn't already showing
      if (!showLoader && !showTimeoutRef.current) {
        showTimeoutRef.current = setTimeout(() => {
          // Double-check we're still loading before showing
          if (isLoadingRef.current) {
            setShowLoader(true);
            spinnerStartTimeRef.current = Date.now();
          }
          showTimeoutRef.current = null;
        }, SPINNER_DELAY);
      }
    } else if (showTimeoutRef.current) {
      // Loading finished - cancel the show timeout
      // Response arrived before delay - handles the < 100ms case (instant feeling)
      // Spinner never appeared, so no need to hide it
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
      spinnerStartTimeRef.current = null;
    } else if (showLoader && spinnerStartTimeRef.current) {
      // Spinner is showing - ensure minimum visible time
      const elapsed = Date.now() - spinnerStartTimeRef.current;
      const remaining = Math.max(0, MIN_VISIBLE_TIME - elapsed);

      hideTimeoutRef.current = setTimeout(() => {
        setShowLoader(false);
        spinnerStartTimeRef.current = null;
        hideTimeoutRef.current = null;
      }, remaining);
    } else {
      // Spinner wasn't showing, just clean up
      setShowLoader(false);
      spinnerStartTimeRef.current = null;
    }

    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, [isLoading, isFetching, showLoader]);

  return showLoader;
};
